import type { VNode } from 'vue'
import { h, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
    VvButton,
    VvButtonGroup,
    VvInputText,
} from '@volverjs/ui-vue/components'
import { useDialog } from './useDialog'

export const useDialogConfirm = () => {
    const { openDialog } = useDialog()
    const { t: $t } = useI18n({
        useScope: 'global',
    })

    /**
     * Ask before doing something, in three parts that a person reads in order.
     *
     * `title` is what is about to happen, short, in the dialog header.
     * `questionLabel` is the question itself. `descriptionLabel` is what it
     * costs: the consequences, secondary, under the question. Splitting them
     * is the whole point: a confirm that puts question and consequences in one
     * paragraph is read as a wall and answered without being read, which is
     * the opposite of what a confirm is for.
     *
     * `danger` draws the confirm button red, for an action that cannot be
     * undone (DESIGN.md, principle 1): the one strong button of the dialog
     * stays one, and its color says what it costs.
     *
     * Resolves `true` from the confirm button and `false` however else the
     * dialog closes (rejecting first with `emitReject`).
     */
    const openDialogConfirm = ({
        emitReject = false,
        confirmLabel,
        cancelLabel,
        questionLabel,
        descriptionLabel,
        onlyConfirm = false,
        passphrase,
        passphraseLabel,
        passphraseHint,
        title,
        danger = false,
    }: {
        emitReject?: boolean
        confirmLabel?: string
        cancelLabel?: string
        questionLabel?: string
        descriptionLabel?: string
        onlyConfirm?: boolean
        passphrase?: string
        passphraseLabel?: string
        passphraseHint?: string
        title?: string
        danger?: boolean
    } = {}) => {
        return new Promise<boolean>((resolve, reject) => {
            const passphraseText = ref<string>()
            const isOpen = openDialog(
                {
                    role: 'alertdialog',
                    size: 'small',
                    keepOpen: true,
                    title,
                },
                {
                    default: () =>
                        h('div', [
                            h(
                                'div',
                                {
                                    class: [
                                        'text-14 text-word-2',
                                        descriptionLabel ? '' : 'mb-sm',
                                    ],
                                    style: 'white-space: pre-line',
                                },
                                questionLabel ?? $t('message.confirm'),
                            ),
                            descriptionLabel
                                ? h(
                                      'p',
                                      {
                                          class: 'text-14 text-word-3 mt-xs mb-sm',
                                          style: 'white-space: pre-line',
                                      },
                                      descriptionLabel,
                                  )
                                : undefined,
                            passphrase
                                ? h(VvInputText, {
                                      modelValue: passphraseText.value,
                                      'onUpdate:modelValue': (
                                          value: string,
                                      ) => {
                                          passphraseText.value = value
                                      },
                                      type: 'text',
                                      name: 'passphrase',
                                      class: 'mb-0',
                                      floating: true,
                                      label:
                                          passphraseLabel ??
                                          $t('label.passphrase'),
                                      hintLabel:
                                          passphraseHint ??
                                          $t('hint.passphrase', { passphrase }),
                                  })
                                : undefined,
                        ]),
                    footer: () =>
                        h(VvButtonGroup, () => {
                            const toReturn: VNode[] = []
                            if (!onlyConfirm) {
                                toReturn.push(
                                    h(VvButton, {
                                        label:
                                            cancelLabel ?? $t('action.cancel'),
                                        modifiers: 'ghost',
                                        onClick: () => {
                                            isOpen.value = false
                                        },
                                    }),
                                )
                            }
                            toReturn.push(
                                h(VvButton, {
                                    label: confirmLabel ?? $t('action.proceed'),
                                    modifiers: danger ? 'danger' : undefined,
                                    disabled: passphrase
                                        ? passphrase !== passphraseText.value
                                        : false,
                                    onClick: () => {
                                        resolve(true)
                                        isOpen.value = false
                                    },
                                }),
                            )
                            return toReturn
                        }),
                },
            )
            // Closing is the answer "no", whatever closes it: Cancel, the
            // close button of the header, or a second Esc, which closes even
            // a `keepOpen` dialog. Without it a caller waited for ever. The
            // confirm button settles the promise first, so this cannot
            // override it.
            const stopWatching = watch(isOpen, (open) => {
                if (open) {
                    return
                }
                stopWatching()
                if (emitReject) {
                    reject()
                }
                resolve(false)
            })
        })
    }

    return { openDialogConfirm }
}
